import { useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Check } from "lucide-react";
import Seo from "../components/Seo";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import FinalCta from "../sections/FinalCta";
import {
  IMG,
  resolveImage,
  WHATSAPP_LINK,
  CONTACT_EMAIL,
  PACKAGES,
  PACKAGE_FEATURES,
  PACKAGE_TERMS,
  ITINERARIES,
} from "../data/content";

export default function PackagesPage() {
  const [active, setActive] = useState(PACKAGES[0].slug);
  const activePackage = PACKAGES.find((p) => p.slug === active) || PACKAGES[0];
  const itinerary = ITINERARIES[active] || [];

  return (
    <main data-testid="packages-page">
      <Seo
        title="Corbett Wildlife Packages | The Jamoon"
        description="Explore curated wildlife, photography, birding and wilderness packages at The Jamoon beside the Ramganga in Corbett, Uttarakhand."
        path="/packages"
        image={IMG.river}
        imageAlt="Elephants beside the Ramganga near The Jamoon in Corbett"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Packages", path: "/packages" }]}
      />
      <PageHero
        testid="packages-hero"
        image={IMG.river}
        imageAlt="Elephants beside the Ramganga near The Jamoon in Corbett"
        eyebrow="Packages & Itineraries"
        title={<>The Jamoon <span className="italic text-brass">Packages</span></>}
        sub="Each plan is a complete, guided wilderness itinerary — transfers, stays, meals and the forest, arranged end to end. All rates are per couple, plus applicable taxes."
      />

      {/* Package cards */}
      <section data-testid="packages-cards" className="mx-auto max-w-7xl px-6 py-24 sm:px-12 sm:py-28 lg:px-20">
        <div className="grid gap-8 lg:grid-cols-2">
          {PACKAGES.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.1}>
              <div
                data-testid={`package-card-${p.slug}`}
                className={`flex h-full flex-col border ${p.featured ? "border-brass/50" : "border-ivory/15"} bg-forest`}
              >
                <div className="relative h-56 overflow-hidden">
                  <img src={resolveImage(IMG[p.image], 900, 520)} alt={p.imageAlt} loading="lazy" className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-forest/90 to-transparent" />
                  {p.featured && (
                    <span className="absolute right-4 top-4 bg-brass px-3 py-1 text-[10px] font-semibold tracking-[0.2em] text-charcoal">
                      SIGNATURE
                    </span>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-8 sm:p-10">
                  <p className="text-[11px] font-medium tracking-[0.3em] text-brass">{p.duration.toUpperCase()}</p>
                  <h2 className="mt-3 font-serif text-2xl font-light text-ivory sm:text-3xl">{p.name}</h2>
                  <p className="mt-3 text-sm leading-relaxed text-ivory/60">{p.tagline}</p>
                  <div className="mt-6 flex flex-wrap items-baseline gap-x-3 gap-y-1 border-y border-ivory/10 py-5">
                    <span className="whitespace-nowrap font-serif text-3xl text-ivory">{p.price}</span>
                    <span className="text-xs text-ivory/50">{p.priceNote}</span>
                  </div>
                  <p className="mt-6 text-[11px] font-medium tracking-[0.25em] text-ivory/45">INCLUSIONS</p>
                  <ul className="mt-4 space-y-3">
                    {p.inclusions.map((inc, idx) => (
                      <li key={idx} className="flex gap-3 text-sm leading-relaxed text-ivory/75">
                        <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-brass" />
                        <span>{inc}</span>
                      </li>
                    ))}
                  </ul>
                  {p.footnote && <p className="mt-5 text-xs italic leading-relaxed text-ivory/40">{p.footnote}</p>}
                  <div className="mt-auto flex flex-col gap-3 pt-8 sm:flex-row">
                    <button
                      data-testid={`package-view-itinerary-${p.slug}`}
                      onClick={() => {
                        setActive(p.slug);
                        document.querySelector("[data-testid=itinerary-section]")?.scrollIntoView({ behavior: "smooth" });
                      }}
                      className="flex-1 border border-brass/40 px-6 py-3.5 text-xs font-semibold tracking-[0.2em] text-brass transition-colors duration-300 hover:bg-brass/10"
                    >
                      VIEW ITINERARY
                    </button>
                    <a
                      data-testid={`package-enquire-${p.slug}`}
                      href={WHATSAPP_LINK}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex flex-1 items-center justify-center gap-2 px-6 py-3.5 text-xs font-semibold tracking-[0.2em] transition-colors duration-300 ${p.featured ? "bg-brass text-charcoal hover:bg-[#B89B6A]" : "bg-brass/90 text-charcoal hover:bg-brass"}`}
                    >
                      <MessageCircle className="h-4 w-4" /> ENQUIRE
                    </a>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Itinerary */}
      <section data-testid="itinerary-section" className="scroll-mt-24 bg-forest py-24 sm:py-28 lg:py-32">
        <div className="mx-auto max-w-5xl px-6 sm:px-12 lg:px-20">
          <Reveal>
            <p className="text-xs font-medium uppercase tracking-[0.3em] text-brass">The Itinerary</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-3xl font-light leading-tight tracking-tight text-ivory sm:text-4xl">
              Day by day, <span className="italic text-brass">at forest pace.</span>
            </h2>
          </Reveal>

          {/* Plan switcher */}
          <div className="mt-10 flex flex-wrap gap-3">
            {PACKAGES.map((p) => (
              <button
                key={p.slug}
                data-testid={`itinerary-tab-${p.slug}`}
                onClick={() => setActive(p.slug)}
                className={`border px-5 py-2.5 text-[11px] font-medium tracking-[0.2em] transition-colors duration-300 ${
                  active === p.slug
                    ? "border-brass bg-brass text-charcoal"
                    : "border-ivory/20 text-ivory/70 hover:border-brass/60 hover:text-brass"
                }`}
              >
                {p.name.toUpperCase()}
              </button>
            ))}
          </div>

          <p className="mt-6 text-sm text-ivory/55">
            {activePackage.duration} · {activePackage.price} {activePackage.priceNote}
          </p>

          <div className="mt-10 space-y-0">
            {itinerary.map((d, i) => (
              <Reveal key={d.day} delay={i * 0.06}>
                <div data-testid={`itinerary-day-${i}`} className="grid gap-4 border-t border-ivory/10 py-8 last:border-b sm:grid-cols-[160px_1fr] sm:gap-10">
                  <div>
                    <p className="text-[11px] font-medium tracking-[0.3em] text-brass">{d.day}</p>
                    <h3 className="mt-2 font-serif text-xl font-light text-ivory">{d.title}</h3>
                  </div>
                  <ul className="space-y-3">
                    {d.items.map((it, idx) => (
                      <li key={idx} className="flex gap-3 text-sm leading-relaxed text-ivory/70">
                        <span className="mt-2 h-1 w-1 flex-shrink-0 rounded-full bg-brass/70" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Every stay includes + Terms */}
      <section className="mx-auto max-w-5xl px-6 py-24 sm:px-12 lg:px-20">
        <Reveal>
          <p className="text-[11px] font-medium tracking-[0.3em] text-brass">EVERY STAY INCLUDES</p>
          <div className="mt-6 grid gap-x-10 gap-y-4 sm:grid-cols-2">
            {PACKAGE_FEATURES.map((f, idx) => (
              <div key={idx} className="flex gap-3 text-sm leading-relaxed text-ivory/75">
                <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-brass/70" />
                <span>{f}</span>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-14 border border-ivory/10 bg-forest p-8 sm:p-10">
            <p className="text-[11px] font-medium tracking-[0.3em] text-brass">TERMS & BOOKING</p>
            <ul className="mt-5 space-y-3">
              {PACKAGE_TERMS.map((t, idx) => (
                <li key={idx} className="flex gap-3 text-sm leading-relaxed text-ivory/55">
                  <span className="mt-1 text-brass/60">—</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm leading-relaxed text-ivory/55">
              For customised itineraries, {" "}
              <Link data-testid="packages-plan-stay-link" to="/plan" className="text-brass underline-offset-4 hover:underline">plan your stay</Link>
              {" "}or write to{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-brass underline-offset-4 hover:underline">{CONTACT_EMAIL}</a>.
            </p>
          </div>
        </Reveal>
      </section>

      <FinalCta />
    </main>
  );
}
